import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, Receipt } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui-kit/Card";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui-kit/Table";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";
import { Select } from "@/components/ui-kit/Select";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui-kit/Dialog";

const CATEGORIES = ["Filing Fee", "Service of Process", "Expert", "Other"];
const STATUSES = ["Not Billed", "Billed", "Reimbursed"];
const todayISO = () => new Date().toISOString().slice(0, 10);

function CostForm({ matterId, existing, onClose, onSaved }) {
  const [form, setForm] = useState(() =>
    existing
      ? { ...existing, amount: String(existing.amount ?? ""), reimbursed_date: existing.reimbursed_date || "", notes: existing.notes || "" }
      : { date: todayISO(), description: "", vendor: "One Legal", category: "Filing Fee", amount: "", reimbursable: true, recovery_status: "Not Billed", reimbursed_date: "", notes: "" }
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const amountNum = parseFloat(form.amount);
  const valid = form.description.trim() && amountNum > 0;

  const save = async () => {
    setBusy(true);
    setError("");
    const payload = {
      date: form.date || todayISO(),
      description: form.description.trim(),
      vendor: form.vendor?.trim() || "One Legal",
      category: form.category,
      amount: amountNum,
      reimbursable: !!form.reimbursable,
      recovery_status: form.recovery_status,
      reimbursed_date: form.recovery_status === "Reimbursed" ? (form.reimbursed_date || todayISO()) : null,
      notes: form.notes?.trim() || null,
    };
    try {
      if (existing) await api.put(`/costs/${existing.id}`, payload);
      else await api.post(`/matters/${matterId}/costs`, payload);
      onSaved();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 py-1">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="cost-date">Date</Label>
          <Input id="cost-date" type="date" data-testid="cost-date-input" value={form.date} onChange={set("date")} />
        </div>
        <div>
          <Label htmlFor="cost-amount">Amount</Label>
          <Input id="cost-amount" type="number" step="0.01" min="0" data-testid="cost-amount-input" value={form.amount} onChange={set("amount")} placeholder="0.00" />
        </div>
      </div>
      <div>
        <Label htmlFor="cost-desc">Description</Label>
        <Input id="cost-desc" data-testid="cost-description-input" value={form.description} onChange={set("description")} placeholder="Court filing fee — Notice of Motion" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="cost-vendor">Vendor</Label>
          <Input id="cost-vendor" data-testid="cost-vendor-input" value={form.vendor} onChange={set("vendor")} placeholder="One Legal" />
        </div>
        <div>
          <Label htmlFor="cost-category">Category</Label>
          <Select id="cost-category" data-testid="cost-category-select" value={form.category} onChange={set("category")}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="cost-status">Recovery status</Label>
          <Select id="cost-status" data-testid="cost-status-select" value={form.recovery_status} onChange={set("recovery_status")}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </div>
        {form.recovery_status === "Reimbursed" && (
          <div>
            <Label htmlFor="cost-reimbursed-date">Reimbursed date</Label>
            <Input id="cost-reimbursed-date" type="date" data-testid="cost-reimbursed-date-input" value={form.reimbursed_date} onChange={set("reimbursed_date")} />
          </div>
        )}
      </div>
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input type="checkbox" data-testid="cost-reimbursable-checkbox" checked={!!form.reimbursable} onChange={set("reimbursable")} className="h-4 w-4 rounded border-border" />
        Reimbursable — bill this cost back to the client
      </label>
      <div>
        <Label htmlFor="cost-notes">Notes</Label>
        <Input id="cost-notes" data-testid="cost-notes-input" value={form.notes} onChange={set("notes")} placeholder="Optional" />
      </div>
      {error && <p className="text-sm text-destructive" data-testid="cost-form-error">{error}</p>}
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="cost-cancel-button">Cancel</Button>
        <Button onClick={save} disabled={busy || !valid} data-testid="cost-save-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {existing ? "Save changes" : "Add cost"}
        </Button>
      </DialogFooter>
    </div>
  );
}

function Chip({ label, value, testid, tone }) {
  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`nums text-sm font-semibold ${tone === "out" ? "text-status-partial" : tone === "in" ? "text-status-paid" : "text-foreground"}`}>{money(value)}</p>
    </div>
  );
}

const STATUS_TONE = { "Reimbursed": "text-status-paid", "Billed": "text-status-sent", "Not Billed": "text-muted-foreground" };

/** Standalone advanced-costs ledger for a matter. Independent of invoices/billing. */
export function CostsPanel({ matterId, version }) {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null);     // {mode:'add'} | {mode:'edit', cost}
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => api.get(`/matters/${matterId}/costs`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [matterId, version]);

  const doDelete = async () => {
    await api.delete(`/costs/${confirmDel.id}`).catch(() => {});
    setConfirmDel(null);
    load();
  };

  const rollup = data?.rollup;
  const rows = data?.costs || [];

  return (
    <Card data-testid="costs-panel">
      <CardHeader className="flex-row items-center justify-between gap-3">
        <CardTitle className="flex items-center gap-2"><Receipt className="h-4 w-4 text-muted-foreground" /> Costs / Disbursements</CardTitle>
        <Button size="sm" onClick={() => setDialog({ mode: "add" })} data-testid="add-cost-button"><Plus className="h-4 w-4" /> Add cost</Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {rollup && (
          <div className="grid grid-cols-3 gap-3">
            <Chip label="Advanced" value={rollup.advanced} testid="costs-rollup-advanced" />
            <Chip label="Reimbursed" value={rollup.reimbursed} testid="costs-rollup-reimbursed" tone="in" />
            <Chip label="Outstanding" value={rollup.outstanding} testid="costs-rollup-outstanding" tone="out" />
          </div>
        )}

        {!data ? (
          <div className="grid place-items-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground" data-testid="costs-empty">No costs logged yet. Add court filing fees and other advanced costs here.</p>
        ) : (
          <Table>
            <THead><TR><TH>Date</TH><TH>Description</TH><TH>Vendor</TH><TH>Category</TH><TH className="text-right">Amount</TH><TH>Status</TH><TH className="text-right">Actions</TH></TR></THead>
            <TBody>
              {rows.map((c) => (
                <TR key={c.id} data-testid={`cost-row-${c.id}`}>
                  <TD className="text-muted-foreground">{shortDate(c.date)}</TD>
                  <TD className="font-medium text-foreground">{c.description}{!c.reimbursable && <span className="ml-2 text-[11px] uppercase text-muted-foreground">(non-billable)</span>}</TD>
                  <TD className="text-muted-foreground">{c.vendor}</TD>
                  <TD className="text-muted-foreground">{c.category}</TD>
                  <TD className="nums text-right">{money(c.amount)}</TD>
                  <TD><span className={`text-xs font-medium ${STATUS_TONE[c.recovery_status] || ""}`}>{c.recovery_status}</span></TD>
                  <TD className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-cost-${c.id}`} onClick={() => setDialog({ mode: "edit", cost: c })}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`delete-cost-${c.id}`} onClick={() => setConfirmDel(c)}><Trash2 className="h-3.5 w-3.5 text-status-void" /></Button>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent className="max-w-lg flex max-h-[90vh] flex-col" data-testid="cost-dialog">
          <DialogTitle>{dialog?.mode === "edit" ? "Edit cost" : "Add cost"}</DialogTitle>
          <DialogDescription>Log a cost the firm advanced on this matter. This is a standalone ledger — it does not appear on any invoice.</DialogDescription>
          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            {dialog && <CostForm matterId={matterId} existing={dialog.mode === "edit" ? dialog.cost : null} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <DialogContent className="max-w-sm" data-testid="cost-delete-dialog">
          <DialogTitle>Delete cost?</DialogTitle>
          <DialogDescription>Remove &ldquo;{confirmDel?.description}&rdquo; ({money(confirmDel?.amount || 0)}) from this matter&apos;s cost ledger. This cannot be undone.</DialogDescription>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDel(null)} data-testid="cost-delete-cancel">Cancel</Button>
            <Button onClick={doDelete} className="bg-status-void text-white hover:bg-status-void/90" data-testid="cost-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
